import {type RefObject, useEffect, useRef} from 'react';
import {useEventCallback, useIntersectionObserver, useIsMounted} from 'usehooks-ts';

const NEXT_REQUEST_DELAY = 300;

type HasMore = boolean;

type Options = {
    intersectionObserverOptions: Parameters<typeof useIntersectionObserver>[1];
    nextRequestDelay: number;
}

export const useLoadWhenIntersecting = (
    ref: RefObject<Element>,
    loadMore: ({signal}: {signal: AbortSignal}) => Promise<HasMore>,
    options: Options = {
        intersectionObserverOptions: {},
        nextRequestDelay: NEXT_REQUEST_DELAY,
    },
) => {
    const abortController = useRef<AbortController>();
    const timeoutId = useRef<number>();
    const entry = useIntersectionObserver(ref, options.intersectionObserverOptions);
    const isMounted = useIsMounted();

    const loadWhenIntersecting = useEventCallback(() => {
        if (!entry?.isIntersecting) {
            return window.clearTimeout(timeoutId.current);
        }

        abortController.current = new AbortController();
        loadMore({signal: abortController.current!.signal}).then((hasMore) => {
            if (!isMounted() || !hasMore) {
                return;
            }

            timeoutId.current = window.setTimeout(loadWhenIntersecting, options.nextRequestDelay);
        });
    });

    useEffect(() => {
        loadWhenIntersecting();

        return () => window.clearTimeout(timeoutId.current);
    }, [entry?.isIntersecting, loadMore, loadWhenIntersecting]);

    useEffect(() => {
        return () => {
            abortController.current?.abort();
        };
    }, []);
};
